import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from 'react';
import {useTranslation} from 'react-i18next';
import {GetShareableLinksResponse} from '../../../../api/types/production.type';
import {
  useConfirmReProduction,
  useGetConfirmViewForReProduction,
  useDownloadProduction,
  useGetShareableLinks,
} from '../../../../hooks/productions/useProductions';
import {formatDateCommon} from '../../../../utils/utils';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import './ReProductionQCConfirmation.scss';
import {
  getReProductionStatusFromStateCode,
  isReProductionCommitFailedState,
  isReProductionCommitWorkflowFailedState,
  isReProductionCommitWorkflowInProgressState,
  isReProductionUncommitFailedState,
  ModuleType,
  ProductionState,
  ProductionStatus,
  ToastType,
  ReProductionFormData,
} from '../ReProductionUtils';
import {GetReProductionConfirmViewResponse} from '../../../../api/types/reproduction.type';
import ErrorGridModal from '../../create-new-production/generate/ErrorModal/ErrorGridModal';
import {ErrorGridType} from '../../create-new-production/generate/ErrorModal/utils';

export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface ReProductionQCConfirmationProps {
  productionId: number;
  securityGroupId?: number;
  productionSetId?: number;
  isProductionSet: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goBack?: () => void;
  productionName?: string;
  productionStatusCode?: string;
  formData?: ReProductionFormData;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  onWizardEditableAfterUncommit?: () => void;
  onCommitUncommitStateChange?: (stateCode: string) => void;
}

const ReProductionQCConfirmation = forwardRef<
  StepChildRef,
  ReProductionQCConfirmationProps
>(
  (
    {
      productionId,
      securityGroupId: securityGroupIdProp,
      isProductionSet,
      productionName,
      productionStatusCode,
      getStatus,
      goBack: _goBack,
      formData,
      onWizardEditableAfterUncommit,
      onCommitUncommitStateChange,
    },
    ref
  ) => {
    const {t} = useTranslation();

    const [showShareableLinksModal, setShowShareableLinksModal] =
      useState<boolean>(false);
    const [ReproductionQCConfirmationData, setReproductionQCConfirmationData] =
      useState<GetReProductionConfirmViewResponse>();
    const [shareableLinks, setShareableLinks] =
      useState<GetShareableLinksResponse>();
    const [disabledCommitOrUncommit, setDisabledCommitOrUncommit] =
      useState<boolean>(false);
    const [isAutoRefresh, setIsAutoRefresh] = useState<boolean>(false);
    const [openErrorGridModal, setOpenErrorGridModal] = useState(false);
    const downloadMenuRef = useRef<ArrMenuHandle | null>(null);

    const selectedSecurityGroupId = securityGroupIdProp ?? 0;

    const moduleType = useMemo(
      () => (isProductionSet ? ModuleType.REPRODUCTION : ModuleType.EXPORT),
      [isProductionSet]
    );

    const priorProductionNames: string[] = useMemo(() => {
      // 1. Prefer names from formData (set by SelectProductions step)
      const namesFromForm = formData?.selectedProductionNames;
      if (namesFromForm?.length) {
        return namesFromForm;
      }
      // 2. Fallback: API response
      const apiPriorName =
        ReproductionQCConfirmationData?.reProductionResultDetails
          ?.priorProductionName;
      return apiPriorName ? [apiPriorName] : [];
    }, [
      formData?.selectedProductionNames,
      ReproductionQCConfirmationData?.reProductionResultDetails
        ?.priorProductionName,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        save: () => {},
        next: () => {},
      }),
      []
    );

    // REPLACE-3: Change HARDCODED_PRODUCTION_ID → productionId
    const {data: confirmationData, refetch: refetchConfirmView} =
      useGetConfirmViewForReProduction(
        {
          productionId: productionId,
          securityGroupId: selectedSecurityGroupId,
          ModuleType: moduleType,
        },
        !!selectedSecurityGroupId && !!productionId
      );

    const {mutate: getShareableLinks} = useGetShareableLinks(
      (data) => setShareableLinks(data),
      (error) => {
        setShowShareableLinksModal(false);
        getStatus(true, ToastType.Error, error?.message);
      }
    );

    const {mutate: downloadProduction} = useDownloadProduction();

    const {mutate: commitUncommitProduction} = useConfirmReProduction();

    // Use live confirmationData, fall back to cached state
    const qcData = useMemo(
      () => confirmationData || ReproductionQCConfirmationData,
      [confirmationData, ReproductionQCConfirmationData]
    );

    // Now that qcData is available, compute these
    const displayProductionName =
      productionName || qcData?.reProductionResultDetails?.productionName || '';

    const currentStateCode =
      qcData?.reProductionResultDetails?.productionStateCode;
    const currentStatusCode =
      qcData?.reProductionResultDetails?.productionStatusCode ??
      productionStatusCode;
    const displayProductionStatus =
      getReProductionStatusFromStateCode(currentStateCode) ??
      qcData?.reProductionResultDetails?.productionStatus;
    const isFailedState =
      isReProductionCommitWorkflowFailedState(currentStateCode);
    const isCommitAction = isReProductionUncommitFailedState(currentStateCode)
      ? false
      : isReProductionCommitFailedState(currentStateCode)
        ? true
        : (qcData?.isCommitOrUncommit ?? true);

    useEffect(() => {
      if (confirmationData) {
        setReproductionQCConfirmationData(confirmationData);
      }
    }, [confirmationData]);

    // Notify parent (ReProduction) whenever the state code changes so it can
    // lock the wizard and control button visibility during commit/uncommit flow.
    useEffect(() => {
      onCommitUncommitStateChange?.(currentStateCode ?? '');
    }, [currentStateCode, onCommitUncommitStateChange]);

    useEffect(() => {
      if (!ReproductionQCConfirmationData) return;
      const shouldAutoRefresh =
        !isFailedState &&
        (currentStatusCode === ProductionStatus.INPROGRESS ||
          currentStateCode === ProductionState.COMMITINPROGRESS ||
          currentStateCode === ProductionState.UNCOMMITINPROGRESS ||
          isReProductionCommitWorkflowInProgressState(currentStateCode));

      if (!isAutoRefresh && shouldAutoRefresh) {
        setIsAutoRefresh(true);
      }
    }, [
      ReproductionQCConfirmationData,
      currentStatusCode,
      currentStateCode,
      isFailedState,
      isAutoRefresh,
    ]);

    useEffect(() => {
      if (showShareableLinksModal && productionId) {
        getShareableLinks({
          productionId: productionId,
          expiryDate:
            qcData?.reProductionResultDetails?.shareableLinksExpireOn ?? '',
          isRegenerate: false,
        });
      }
    }, [showShareableLinksModal]);

    useEffect(() => {
      if (!isAutoRefresh) return;

      const interval = setInterval(async () => {
        const refreshed = await refetchConfirmView();

        const status =
          refreshed?.data?.reProductionResultDetails?.productionStatusCode;
        const state =
          refreshed?.data?.reProductionResultDetails?.productionStateCode;

        if (
          status === ProductionStatus.COMPLETED ||
          status === ProductionStatus.DRAFT
        ) {
          clearInterval(interval);
          setIsAutoRefresh(false);
          setDisabledCommitOrUncommit(false);
          setReproductionQCConfirmationData(refreshed.data);
          if (status === ProductionStatus.DRAFT) {
            onWizardEditableAfterUncommit?.();
          }
        } else if (isReProductionUncommitFailedState(state)) {
          clearInterval(interval);
          setIsAutoRefresh(false);
          setDisabledCommitOrUncommit(false);
          setReproductionQCConfirmationData(refreshed.data);
          getStatus(
            true,
            ToastType.Error,
            t('reProduction.qcConfirmation.errors.uncommitFailed')
          );
        } else if (isReProductionCommitFailedState(state)) {
          clearInterval(interval);
          setIsAutoRefresh(false);
          setDisabledCommitOrUncommit(false);
          setReproductionQCConfirmationData(refreshed.data);
          getStatus(
            true,
            ToastType.Error,
            t('reProduction.qcConfirmation.errors.commitFailed')
          );
        }
      }, 5000);

      return () => clearInterval(interval);
    }, [
      isAutoRefresh,
      refetchConfirmView,
      getStatus,
      onWizardEditableAfterUncommit,
      t,
    ]);

    const downloadMenu = useMemo(() => {
      return [
        {
          id: 'downloadProduction',
          label: t('reProduction.qcConfirmation.downloadFullProduction'),
          disabled: !qcData?.reProductionResultDetails?.productionArchivePath,
          onClick: () => handleDownloadAllFile(),
        },
        {
          id: 'downloadDat',
          label: t('reProduction.qcConfirmation.downloadDat'),
          disabled: !qcData?.reProductionResultDetails?.productionDATFilePath,
          onClick: () => handleDownloadDAT(),
        },
        {
          id: 'copyPath',
          label: t('reProduction.qcConfirmation.copyPath'),
          disabled: false,
          onClick: () => handleCopyToClipboard(),
        },
      ];
    }, [qcData]);

    const handleCopyToClipboard = () => {
      navigator.clipboard.writeText(
        qcData?.reProductionResultDetails?.productionDestinationPath ?? ''
      );
    };

    const handleDownloadDAT = () => {
      downloadProduction(
        {
          sourcePath:
            qcData?.reProductionResultDetails?.productionDATFilePath ?? '',
          fileName: `${displayProductionName}_DAT`,
        },
        {
          onSuccess: () => {
            getStatus(
              true,
              ToastType.Success,
              t('reProduction.qcConfirmation.datDownload')
            );
          },
          onError: (error) => {
            getStatus(true, ToastType.Error, error?.message);
          },
        }
      );
    };

    const handleDownloadAllFile = () => {
      downloadProduction(
        {
          sourcePath:
            qcData?.reProductionResultDetails?.productionArchivePath ?? '',
          fileName: `${displayProductionName}`,
        },
        {
          onSuccess: () => {
            getStatus(
              true,
              ToastType.Success,
              t('reProduction.qcConfirmation.allFileDownload')
            );
          },
          onError: (error) => {
            getStatus(true, ToastType.Error, error?.message);
          },
        }
      );
    };

    const handleCommitUncommit = (isCommit: boolean) => {
      setDisabledCommitOrUncommit(true);
      const payload = {
        ProductionId: productionId,
        SecurityGroupId: selectedSecurityGroupId,
        ProductionLabel: displayProductionName,
        IsCommit: isCommit, // true = commit, false = uncommit
      };
      commitUncommitProduction(payload, {
        onSuccess: (data) => {
          getStatus(true, ToastType.Success, data.message);
          setIsAutoRefresh(true);
        },
        onError: (error) => {
          getStatus(true, ToastType.Error, error?.message);
          setDisabledCommitOrUncommit(false);
        },
      });
    };

    const onDownloadBtnClick = (e: React.MouseEvent<HTMLElement>) => {
      downloadMenuRef.current?.open(e.currentTarget as HTMLElement);
    };

    const onMenuItemClick = (item: CustomMenuItem) => {
      item.onClick?.();
    };

    const handleRegenerate = async () => {
      getShareableLinks({
        productionId: productionId,
        expiryDate:
          qcData?.reProductionResultDetails?.shareableLinksExpireOn ?? '',
        isRegenerate: true,
      });
    };

    const canShowNativelyPlaceholderErrorDocs =
      (qcData?.reProductionResultDetails?.nativelyGeneratedDocs ?? 0) > 0;

    return (
      <Box
        className="quality-form-section"
        component="form"
        data-testid="quality-form"
      >
        <Box className="quality-container">
          <Box className="quality-header">
            <Typography id="quality-header" className="title-text">
              {t('reProduction.qcConfirmation.title')}
            </Typography>
          </Box>

          <Box className="quality-body" aria-labelledby="quality-body">
            <Box
              className="quality-production-container"
              aria-labelledby="quality-production-container"
            >
              <Box
                className="quality-production-info"
                aria-labelledby="quality-production-info"
              >
                <Typography
                  data-testid="productionName"
                  className="typo-text subhead1_size custom-text-headings"
                >
                  {displayProductionName}
                </Typography>

                <Box
                  data-testid="productionStatusContainer"
                  className="production-sub-container"
                  aria-labelledby="quality-production-info"
                >
                  <Typography
                    data-testid="production-status-text"
                    className="typo-text label1_size custom-body-strong minWidthForText"
                  >
                    {t('reProduction.qcConfirmation.status')}
                  </Typography>
                  <Typography
                    className={`typo-text label1_size ${
                      isFailedState
                        ? 'custom-text-failed'
                        : 'custom-text-success'
                    }`}
                    data-testid="production-status"
                  >
                    {displayProductionStatus}
                  </Typography>
                </Box>

                <Box
                  data-testid="documentsGeneratedContainer"
                  className="production-sub-container"
                  aria-labelledby="quality-production-info"
                >
                  <Typography
                    data-testid="production-documents-generated-text"
                    className="typo-text label1_size custom-body-strong minWidthForText"
                  >
                    {t('reProduction.qcConfirmation.documentsGenerated')}
                  </Typography>
                  <Typography
                    className="typo-text label1_size custom-text-headings"
                    data-testid="documents-generated"
                  >
                    {
                      qcData?.reProductionResultDetails
                        ?.numberOfDocumentsGenerated
                    }
                  </Typography>
                </Box>

                {/* {priorProductionNames.length > 0 && ( */}
                <Box
                  data-testid="priorProductionsContainer"
                  className="production-sub-container production-sub-container--prior"
                  aria-labelledby="quality-production-info"
                >
                  <Typography
                    data-testid="production-prior-productions-text"
                    className="typo-text label1_size custom-body-strong minWidthForText"
                  >
                    {t('reProduction.qcConfirmation.priorProductionsLabel')}
                  </Typography>
                  <Box
                    className="prior-productions-list"
                    data-testid="prior-productions-list"
                  >
                    {priorProductionNames.length > 0 ? (
                      priorProductionNames.map((name, idx) => (
                        <Typography
                          key={idx}
                          className="typo-text label1_size custom-text-headings"
                          data-testid="prior-production-name"
                        >
                          {name}
                        </Typography>
                      ))
                    ) : (
                      // ← Show placeholder when no data yet
                      <Typography
                        className="typo-text label1_size custom-text-headings"
                        data-testid="prior-production-name"
                      >
                        ---
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* )} */}
              </Box>

              <Box
                data-testid="actionButtonsContainer"
                className="quality-button-container"
                aria-labelledby="quality-button-container"
              >
                <Box
                  className="quality-production-buttons"
                  aria-labelledby="quality-production-buttons"
                  data-testid="quality-production-buttons"
                >
                  <ArrButton
                    variant="secondary"
                    size="large"
                    onClick={onDownloadBtnClick}
                    endIcon={<ArrowDropDownIcon />}
                    dataTestId="downloadBtn"
                    className="custom-button"
                  >
                    {t('reProduction.qcConfirmation.download')}
                  </ArrButton>

                  <ArrButton
                    variant="secondary"
                    size="large"
                    onClick={() => setShowShareableLinksModal(true)}
                    dataTestId="getShareableLinkBtn"
                    disabled={
                      qcData &&
                      !qcData?.reProductionResultDetails?.shareableLinksExpireOn
                    }
                  >
                    {t('reProduction.qcConfirmation.getShareableLink')}
                  </ArrButton>

                  <ArrButton
                    variant="primary"
                    size="large"
                    onClick={() => handleCommitUncommit(isCommitAction)}
                    dataTestId="commitUncommitBtn"
                    disabled={
                      disabledCommitOrUncommit ||
                      isAutoRefresh ||
                      !!qcData?.disableCommitButton
                    }
                  >
                    {isCommitAction
                      ? t('reProduction.qcConfirmation.commit')
                      : t('reProduction.qcConfirmation.uncommit')}
                  </ArrButton>
                </Box>

                {qcData &&
                  !qcData?.reProductionResultDetails?.productionArchivePath && (
                    <Box
                      className="disable-message"
                      data-testid="download-disabled-container"
                    >
                      <Typography
                        id="download-disabled-msg"
                        className="typo-text body3_size disable-text"
                        data-testid="download-disabled-text"
                      >
                        {isProductionSet
                          ? t('reProduction.qcConfirmation.downloadDisableMsg')
                          : t(
                              'reProduction.qcConfirmation.downloadDisableMsgExport'
                            )}
                      </Typography>
                    </Box>
                  )}
              </Box>
            </Box>

            <Box
              className="quality-production-status"
              aria-labelledby="quality-production-status"
              data-testid="quality-production-status"
            >
              <TableContainer
                className="production-status-table"
                data-testid="production-status-table"
              >
                <Table>
                  <TableHead
                    className="table-header"
                    data-testid="production-status-table-header"
                  >
                    <TableRow>
                      <TableCell
                        className="table-cell border-right"
                        data-testid="production-status-table-header-cell"
                      >
                        {t('reProduction.qcConfirmation.automatedCheckLabel')}
                      </TableCell>
                      <TableCell
                        className="table-cell"
                        data-testid="production-status-table-header-cell"
                      >
                        {t('reProduction.qcConfirmation.statusTitle')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {qcData?.reProductionResultDetails?.qcResultDataList?.map(
                      (qcItem, idx) => (
                        <TableRow
                          key={qcItem.qcLabel || idx}
                          data-testid="table-body-row"
                        >
                          <TableCell
                            className="table-body-cell"
                            title={qcItem.qcHelpText}
                          >
                            {qcItem.qcType}
                          </TableCell>
                          <TableCell
                            className={`table-body-cell ${
                              qcItem.qcResult === 'Success'
                                ? 'success-body-cell'
                                : 'error-body-cell'
                            }`}
                          >
                            {qcItem.qcResult}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {canShowNativelyPlaceholderErrorDocs && (
              <Box
                className="placeholder-error"
                aria-labelledby="placeholder-error"
              >
                <ArrButton
                  variant="text"
                  size="medium"
                  onClick={() => {
                    setOpenErrorGridModal(true);
                  }}
                  dataTestId="placeholderError"
                >
                  {t(
                    'productionsAndExports.createNewProduction.QCConfirmation.placeHolderError',
                    {
                      listOfError:
                        qcData?.reProductionResultDetails
                          ?.nativelyGeneratedDocs ?? 0,
                    }
                  )}
                </ArrButton>
              </Box>
            )}
          </Box>
        </Box>

        <ArrMenu
          id="download-menu"
          ref={downloadMenuRef}
          className="quality-form-section__custom-download-menu"
          items={downloadMenu}
          onItemClick={onMenuItemClick}
          direction="bottom-left"
          data-testid="download-menu"
        />

        {shareableLinks && (
          <ArrModal
            showModal={showShareableLinksModal}
            showClose={true}
            onClose={() => setShowShareableLinksModal(false)}
            title={t('reProduction.qcConfirmation.shareableLinksTitle')}
            description={t('reProduction.qcConfirmation.shareableLinksDesc1')}
            dataTestId="shareLinksModal"
            className="quality-form-section__modal-container"
            buttons={[
              {
                id: '1',
                label: 'Regenerate Links',
                onClick: () => handleRegenerate(),
                variant: 'primary',
                dataTestId: 'regenerate-btn',
              },
            ]}
          >
            <Box
              className="modal-child-container"
              data-testid="modal-child-container"
            >
              <Typography
                id="linkExpiry"
                className="typo-text body2_size link-expiry-text"
                data-testid="link-expiry-text"
              >
                {t('reProduction.qcConfirmation.shareableLinksDesc2', {
                  linkExpiry: formatDateCommon(
                    shareableLinks.newExpiryDate,
                    'YYYY/MM/DD'
                  ),
                })}
              </Typography>
              <ArrTextField
                id="allFile"
                data-testid="allFileInput"
                label={t('reProduction.qcConfirmation.allFiles')}
                value={shareableLinks?.allFilesURL}
                sizeVariant="large"
                slotProps={{
                  input: {
                    'aria-label': t('reProduction.qcConfirmation.allFiles'),
                    'aria-labelledby': 'allFile',
                  },
                }}
                dataTestId={'allFile'}
              />
              <ArrTextField
                id="DATFileOnly"
                data-testid="DATFileOnly"
                label={t('reProduction.qcConfirmation.DATFileOnly')}
                value={shareableLinks?.datFileURL}
                sizeVariant="large"
                slotProps={{
                  input: {
                    'aria-label': t('reProduction.qcConfirmation.DATFileOnly'),
                    'aria-labelledby': 'DATFileOnly',
                  },
                }}
                dataTestId={'DATFileOnly'}
              />
              <Typography
                data-testid="passwordTitle"
                id="passwordTitle"
                className="typo-text body2_size password-title"
              >
                {t('reProduction.qcConfirmation.passwordTitle')}
                <br />
                <span className="typo-text body2_size custom-text-headings">
                  {shareableLinks?.linkPassword}
                </span>
              </Typography>
            </Box>
          </ArrModal>
        )}
        {openErrorGridModal && (
          <ErrorGridModal
            productionId={productionId}
            securityGroupId={selectedSecurityGroupId}
            open={openErrorGridModal}
            onClose={() => setOpenErrorGridModal(false)}
            title={t('reProduction.generate.modals.errors.reProductionErrors')}
            gridType={ErrorGridType.PlaceHolderErrors}
            productionName={displayProductionName}
            dataTestId="reprod-qc-placeholder-error-grid-modal"
          />
        )}
      </Box>
    );
  }
);

ReProductionQCConfirmation.displayName = 'ReProductionQCConfirmation';

export default ReProductionQCConfirmation;
